import {
    IconChevronRight,
    IconProps as TablerIconProps,
} from '@tabler/icons-react';
import classNames from 'classnames';
import * as Flowbite from 'flowbite-react';
import React, {
    ComponentProps,
    PropsWithChildren,
    ReactNode,
    useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getReplaceThemeProps } from 'utils/replaceTheme';
import { useSiteSelectorContext } from '../SiteSelectorContext';

const theme = {
    root: {
        base: 'border border-gray-200 rounded-md',
        flush: {
            off: '',
            on: '',
        },
    },
    content: {
        base: 'pb-2 pl-7 pr-2.5 pt-1',
    },
    title: {
        arrow: {
            base: 'flex-none h-4 w-4',
            open: {
                off: '',
                on: 'rotate-90',
            },
        },
        base: 'disabled:text-disabled [&:disabled_>_svg]:invisible flex flex-row-reverse gap-1 p-2 text-gray-700 w-full',
        flush: {
            off: '',
            on: '',
        },
        heading: 'flex-1 font-bold text-base/none text-left',
        open: {
            off: '',
            on: 'pb-0',
        },
    },
};

const ArrowIcon = ({ color, size, stroke, ...props }: TablerIconProps) => (
    <IconChevronRight
        stroke={color}
        strokeWidth={stroke}
        width={size}
        {...props}
    />
);

interface SiteGroupProps
    extends
        Pick<ComponentProps<typeof Flowbite.Accordion>, 'className'>,
        PropsWithChildren {
    id: number | string;
    sites_count: number;
    title: ReactNode | undefined;
}

export const SiteGroup = ({
    children,
    className,
    id,
    sites_count,
    title,
}: SiteGroupProps) => {
    const { t } = useTranslation('navigation', {
        keyPrefix: 'site_selector.lists.site_group',
    });

    const {
        groupExpandedState,
        groupExpandedStateExclude,
        setGroupExpandedStateExclude,
    } = useSiteSelectorContext();

    const isDisabled = !sites_count;

    const isOpen =
        !isDisabled &&
        ((groupExpandedState === 'collapsed' &&
            groupExpandedStateExclude.includes(id)) ||
            (groupExpandedState === 'expanded' &&
                !groupExpandedStateExclude.includes(id)));

    const setOpen = useCallback(
        () =>
            setGroupExpandedStateExclude((currentGroupExpandedStateExclude) =>
                currentGroupExpandedStateExclude.includes(id)
                    ? currentGroupExpandedStateExclude.filter(
                          (excludeId) => excludeId !== id
                      )
                    : [...currentGroupExpandedStateExclude, id]
            ),
        [id, setGroupExpandedStateExclude]
    );

    return (
        /*
         * `Flowbite.Accordion` component always overrides its children
         * `Flowbite.Accordion.Panel` component props and by that it ultimately
         * does not allow accordion component to be controlled. Work around this
         * issue by replacing recommended `Flowbite.Accordion` wrapper with
         * dummy `div` instead.
         */
        <div className={classNames(theme.root.base, className)}>
            <Flowbite.AccordionPanel
                key={String(isOpen)} // Re-render site nodes on expand/collapse for descendant style-related introspection
                arrowIcon={ArrowIcon}
                isOpen={isOpen}
                setOpen={setOpen}
            >
                <Flowbite.AccordionTitle
                    disabled={isDisabled}
                    {...getReplaceThemeProps(theme.title)}
                >
                    {title}
                    <span className="block font-semibold my-px text-xs text-teal-600">
                        {t('sites_count', { sites_count })}
                    </span>
                </Flowbite.AccordionTitle>

                <Flowbite.AccordionContent
                    {...getReplaceThemeProps(theme.content)}
                >
                    {children}
                </Flowbite.AccordionContent>
            </Flowbite.AccordionPanel>
        </div>
    );
};
